import { apiClient } from '@/core/api/client'
import { Preferences, PreferencesResponse } from '../types/preferences.types'

const formatTimeSlot = (time: string): string => {
  if (!time || time === '') return ''
  // Handle HH:MM:SS format from API to HH:MM format for UI
  const parts = time.split(':')
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`
  }
  return time
}

const formatTimeSlotForAPI = (time: string): string => {
  if (!time || time === '') return ''
  // Convert HH:MM to HH:MM:SS for API
  if (time.match(/^\d{2}:\d{2}$/)) {
    return `${time}:00`
  }
  return time
}

const transformAPIResponseToPreferences = (apiData: any): Preferences => {
  const transformed: Preferences = {
    informations: {
      domicileAddresses: apiData.informations?.domicileAddresses?.map((addr: any) => ({
        id: addr.id,
        name: addr.name || '',
        street: addr.street || '',
        city: addr.city || '',
        postalCode: addr.postalCode || '',
        isSaved: addr.isSaved
      })) || [],
      phoneNumber: apiData.informations?.phoneNumber || '',
      email: apiData.informations?.email || '',
      emergencyContact: apiData.informations?.emergencyContact || ''
    },
    availability: {},
    expertiseTypes: apiData.expertiseTypes || {
      expertisesImmobilieres: false,
      certificationPEB: false,
      permisLocation: false
    },
    selectedProvinces: apiData.selectedProvinces || {},
    selectedCommunes: apiData.selectedCommunes || {},
    maxExpertisesPerDay: apiData.maxExpertisesPerDay || 3,
    preferredLanguage: apiData.preferredLanguage || 'fr',
    notificationPreferences: apiData.notificationPreferences || {
      email: true,
      sms: true,
      push: false
    }
  }

  // Transform availability data - now expecting single slot
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  days.forEach(day => {
    const dayData = apiData.availability?.[day]
    if (dayData?.slot) {
      // New structure with single slot
      transformed.availability[day] = {
        startingDomicile: dayData.startingDomicile || 1,
        slots: [{
          start: formatTimeSlot(dayData.slot.start || ''),
          end: formatTimeSlot(dayData.slot.end || ''),
          enabled: dayData.slot.enabled || false
        }]
      }
    } else {
      // Default structure for missing days
      transformed.availability[day] = {
        startingDomicile: 1,
        slots: [{ start: '09:00', end: '17:00', enabled: false }]
      }
    }
  })

  return transformed
}

const transformPreferencesForAPI = (preferences: Preferences): any => {
  const transformed: any = {
    informations: {
      domicileAddresses: preferences.informations.domicileAddresses.map(addr => ({
        id: addr.id,
        name: addr.name,
        street: addr.street,
        city: addr.city,
        postalCode: addr.postalCode,
        isSaved: addr.isSaved !== undefined ? addr.isSaved : true
      })),
      phoneNumber: preferences.informations.phoneNumber,
      email: preferences.informations.email,
      emergencyContact: preferences.informations.emergencyContact
    },
    availability: {},
    expertiseTypes: preferences.expertiseTypes,
    selectedProvinces: preferences.selectedProvinces,
    selectedCommunes: preferences.selectedCommunes,
    maxExpertisesPerDay: preferences.maxExpertisesPerDay,
    preferredLanguage: preferences.preferredLanguage
  }

  // Transform availability for API - now sending single slot
  Object.keys(preferences.availability).forEach(day => {
    const dayData = preferences.availability[day]
    
    // Get the single slot from first item in slots array
    let singleSlot = dayData.slots && dayData.slots.length > 0 ? dayData.slots[0] : null
    if (!singleSlot) {
      singleSlot = { start: '09:00', end: '17:00', enabled: false }
    }
    
    transformed.availability[day] = {
      startingDomicile: dayData.startingDomicile || 1,
      slot: {
        start: formatTimeSlotForAPI(singleSlot.start),
        end: formatTimeSlotForAPI(singleSlot.end),
        enabled: singleSlot.enabled
      }
    }
  })

  return transformed
}

export const preferencesAPI = {
  getPreferences: async (): Promise<Preferences> => {
    try {
      const response = await apiClient.get<PreferencesResponse>('/api/expert/preferences')
      if (response.success && response.data) {
        return transformAPIResponseToPreferences(response.data)
      }
      throw new Error(response.error || 'Failed to fetch preferences')
    } catch (error) {
      console.error('Error fetching preferences:', error)
      throw error
    }
  },
  
  savePreferences: async (data: Preferences): Promise<{ success: boolean }> => {
    try {
      console.log('API: savePreferences called with data:', data)
      
      // Transform data for API format
      const apiData = transformPreferencesForAPI(data)
      console.log('Sending to API endpoint with transformed data')
      
      const response = await apiClient.post<{ success: boolean }>('/api/expert/preferences', { preferences: apiData })
      console.log('API response:', response)
      return response
    } catch (error) {
      console.error('Error saving preferences:', error)
      throw error
    }
  },
  
  deletePreferences: async (): Promise<{ success: boolean }> => {
    try {
      const response = await apiClient.delete<{ success: boolean }>('/api/expert/preferences')
      return response
    } catch (error) {
      console.error('Error deleting preferences:', error)
      throw error
    }
  },
  
  getMapData: async () => {
    try {
      const response = await apiClient.get('/api/geo/belgium')
      return response
    } catch (error) {
      console.error('Error fetching map data:', error)
      throw error
    }
  },
  
  getProvinces: async () => {
    try {
      const response = await apiClient.get('/api/geo/belgium/provinces')
      return response
    } catch (error) {
      console.error('Error fetching provinces:', error)
      throw error
    }
  },
  
  getCommunes: async (provinceCode?: string) => {
    try {
      const url = provinceCode 
        ? `/api/geo/belgium/communes?province=${provinceCode}`
        : '/api/geo/belgium/communes'
      const response = await apiClient.get(url)
      return response
    } catch (error) {
      console.error('Error fetching communes:', error)
      throw error
    }
  }
}